import { SyntheticEvent, useEffect, useRef, useState } from 'react'

import { setForwardedVideoRef } from './VideoPlayer.utils'
import { UseVideoPlayerControllerOptions } from './useVideoPlayerController.types'
import { useVideoVolume } from './useVideoVolume'

export const useVideoPlayerController = ({
  forwardedRef,
  src,
  onDurationChange,
  onEnded,
  onLoadedMetadata,
  onPause,
  onPlay,
  onRateChange,
  onTimeUpdate,
  onVolumeChange
}: UseVideoPlayerControllerOptions) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const {
    handleVolumeChange,
    isMuted,
    setVolumeFromSlider,
    syncVolume,
    toggleMute,
    volume
  } = useVideoVolume({ videoRef, onVolumeChange })

  useEffect(() => {
    setCurrentTime(0)
    setDuration(0)
    setIsPlaying(false)
  }, [src])

  const setVideoRef = (element: HTMLVideoElement | null) => {
    videoRef.current = element
    setForwardedVideoRef(forwardedRef, element)
  }

  const syncDuration = (video: HTMLVideoElement) => {
    setDuration(Number.isFinite(video.duration) ? video.duration : 0)
  }

  const handleLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget
    syncDuration(video)
    setCurrentTime(video.currentTime)
    syncVolume(video)
    setPlaybackRate(video.playbackRate)
    onLoadedMetadata?.(event)
  }

  const handleDurationChange = (event: SyntheticEvent<HTMLVideoElement>) => {
    syncDuration(event.currentTarget)
    onDurationChange?.(event)
  }

  const handleTimeUpdate = (event: SyntheticEvent<HTMLVideoElement>) => {
    setCurrentTime(event.currentTarget.currentTime)
    onTimeUpdate?.(event)
  }

  const handleRateChange = (event: SyntheticEvent<HTMLVideoElement>) => {
    setPlaybackRate(event.currentTarget.playbackRate)
    onRateChange?.(event)
  }

  const handlePlay = (event: SyntheticEvent<HTMLVideoElement>) => {
    setIsPlaying(true)
    onPlay?.(event)
  }

  const handlePause = (event: SyntheticEvent<HTMLVideoElement>) => {
    setIsPlaying(false)
    onPause?.(event)
  }

  const handleEnded = (event: SyntheticEvent<HTMLVideoElement>) => {
    setIsPlaying(false)
    onEnded?.(event)
  }

  const togglePlayback = () => {
    const video = videoRef.current
    if (!video) return

    if (video.paused || video.ended) {
      void video.play().catch(() => setIsPlaying(false))
    } else {
      video.pause()
    }
  }

  const seek = (value: number) => {
    const video = videoRef.current
    if (!video) return

    video.currentTime = value
    setCurrentTime(value)
  }

  const setRate = (value: number) => {
    const video = videoRef.current
    if (!video) return

    video.playbackRate = value
    setPlaybackRate(value)
  }

  return {
    state: {
      currentTime,
      duration,
      isMuted,
      isPlaying,
      playbackRate,
      volume
    },
    actions: {
      seek,
      setRate,
      setVolumeFromSlider,
      toggleMute,
      togglePlayback
    },
    eventHandlers: {
      onDurationChange: handleDurationChange,
      onEnded: handleEnded,
      onLoadedMetadata: handleLoadedMetadata,
      onPause: handlePause,
      onPlay: handlePlay,
      onRateChange: handleRateChange,
      onTimeUpdate: handleTimeUpdate,
      onVolumeChange: handleVolumeChange
    },
    setVideoRef,
    videoRef
  }
}
